import { useState, useEffect } from "react";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { format } from "date-fns";
import { Calendar, Clock, LogOut, Plus, CheckCircle2, Circle, ChevronRight, CalendarDays, PartyPopper, Flame, Timer, AlertCircle, ChevronDown, SkipForward } from "lucide-react";
import { Link } from "react-router-dom";
import AppLogo from "@/components/AppLogo";

// Helper function to get greeting based on time
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

// Helper function to format countdown
const formatCountdown = (targetTime, targetDate, isToday) => {
  const now = new Date();
  const [hours, minutes] = targetTime.split(":").map(Number);
  const target = new Date(targetDate);
  target.setHours(hours, minutes, 0, 0);
  
  const diff = target - now;
  if (diff < 0) return null;
  
  const totalMinutes = Math.floor(diff / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  
  if (h > 0) {
    return `${h}h ${m}m`;
  }
  return `${m}m`;
};

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const [todayData, setTodayData] = useState(null);
  const [upcomingData, setUpcomingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [streak, setStreak] = useState(0);
  const [nextDose, setNextDose] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [showAllOverdue, setShowAllOverdue] = useState(false);

  const fetchData = async () => {
    try {
      // Get client's local date in YYYY-MM-DD format
      const clientDate = format(new Date(), "yyyy-MM-dd");
      
      const [todayRes, upcomingRes, streakRes, nextDoseRes] = await Promise.all([
        api.get(`/protocols/today?client_date=${clientDate}`),
        api.get(`/protocols/upcoming?client_date=${clientDate}`),
        api.get("/user/streak"),
        api.get(`/user/next-dose?client_date=${clientDate}`)
      ]);
      setTodayData(todayRes.data);
      setUpcomingData(upcomingRes.data);
      setStreak(streakRes.data.streak || 0);
      setNextDose(nextDoseRes.data.next_dose);
    } catch (error) {
      toast.error("Failed to load protocols");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Update countdown every minute
  useEffect(() => {
    if (!nextDose) {
      setCountdown(null);
      return;
    }

    const updateCountdown = () => {
      const formatted = formatCountdown(nextDose.time_of_day, nextDose.date, nextDose.is_today);
      setCountdown(formatted);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [nextDose]);

  const handleDoseToggle = async (dose) => {
    try {
      if (dose.is_completed) {
        await api.delete(`/dose-logs/${dose.dose_log_id}`);
        toast.success("Dose unmarked");
      } else {
        // Use the dose's scheduled_date for logging (important for overdue doses)
        const completedLocalDate = dose.scheduled_date || format(new Date(), "yyyy-MM-dd");
        
        await api.post("/dose-logs", {
          protocol_id: dose.protocol_id,
          compound_name: dose.compound_name,
          dose: dose.dose,
          dose_unit: dose.dose_unit,
          notes: dose.notes,
          completed_local_date: completedLocalDate
        });
        toast.success("Dose logged!");
      }
      fetchData();
    } catch (error) {
      toast.error("Failed to update dose");
    }
  };

  const handleSkipDose = async (dose) => {
    try {
      await api.post("/doses/skip", {
        protocol_id: dose.protocol_id,
        scheduled_date: dose.scheduled_date
      });
      toast.success("Dose skipped");
      fetchData();
    } catch (error) {
      toast.error("Failed to skip dose");
    }
  };

  const today = new Date();
  const formattedDate = format(today, "EEEE, MMMM d");
  const greeting = getGreeting();

  // Helper function to check if a dose is overdue
  const isDoseOverdue = (dose) => {
    if (dose.is_completed) return false;
    
    const now = new Date();
    const [hours, minutes] = dose.time_of_day.split(':').map(Number);
    const doseTime = new Date();
    doseTime.setHours(hours, minutes, 0, 0);
    
    return now > doseTime;
  };

  // Sort doses by time
  const sortedDoses = todayData?.doses ? [...todayData.doses].sort((a, b) => {
    return a.time_of_day.localeCompare(b.time_of_day);
  }) : [];
  
  // Overdue doses from previous days
  const overdueDoses = todayData?.overdue_doses || [];
  const totalOverdueCount = todayData?.total_overdue_count || 0;
  const displayedOverdueDoses = showAllOverdue ? overdueDoses : overdueDoses.slice(0, 2);
  const hasMoreOverdue = totalOverdueCount > 2;
  
  const completedCount = todayData?.doses?.filter(d => d.is_completed).length || 0;
  const totalCount = todayData?.doses?.length || 0;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const allCompleted = completedCount === totalCount && totalCount > 0;

  // Group upcoming doses by day and sort by time within each day
  const groupedUpcoming = upcomingData?.doses?.reduce((groups, dose) => {
    const key = dose.day_label;
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(dose);
    return groups;
  }, {}) || {};

  // Sort doses within each day by time
  Object.keys(groupedUpcoming).forEach(day => {
    groupedUpcoming[day].sort((a, b) => a.time_of_day.localeCompare(b.time_of_day));
  });

  const upcomingDays = Object.keys(groupedUpcoming);

  return (
    <div className="page-container" data-testid="dashboard-page">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <AppLogo size="small" showText={false} />
            <div>
              <p className="text-xs text-muted-foreground">{formattedDate}</p>
              <h1 className="font-heading text-lg font-bold">{greeting}, {user?.name?.split(' ')[0] || 'there'}!</h1>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              className="h-9 w-9 rounded-full"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="p-4 space-y-6">
        {/* Streak and Next Dose Row */}
        {!loading && (streak > 0 || nextDose) && (
          <div className="flex items-center gap-3 animate-fadeIn">
            {/* Streak Counter */}
            {streak > 0 && (
              <div className="flex items-center gap-2 bg-orange-100 text-orange-700 px-3 py-1.5 rounded-full">
                <Flame size={16} className="text-orange-500" />
                <span className="text-sm font-semibold">{streak}-day streak</span>
              </div>
            )}
            
            {/* Next Dose Countdown */}
            {nextDose && countdown && (
              <div className="flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-full">
                <Timer size={16} />
                <span className="text-sm font-semibold">
                  Next dose in {countdown}
                </span>
              </div>
            )}
            
            {/* Next dose tomorrow */}
            {nextDose && !nextDose.is_today && (
              <div className="flex items-center gap-2 bg-muted text-muted-foreground px-3 py-1.5 rounded-full">
                <CalendarDays size={16} />
                <span className="text-sm font-medium">
                  Next dose tomorrow at {nextDose.time_of_day}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Reminder Banner */}
        {!loading && totalCount > 0 && !allCompleted && (
          <div className="bg-primary/10 rounded-xl p-3 flex items-center gap-3 animate-fadeIn">
            <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Clock size={16} className="text-primary" />
            </div>
            <p className="text-sm font-medium">
              You have <span className="text-primary">{totalCount - completedCount} dose{totalCount - completedCount !== 1 ? 's' : ''}</span> scheduled today.
            </p>
          </div>
        )}

        {/* Progress Card */}
        {!loading && totalCount > 0 && (
          <Card className="animate-fadeIn border-primary/20 bg-primary/5">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-muted-foreground">
                  Today's Progress
                </span>
                <span className="text-sm font-semibold">
                  {completedCount} / {totalCount} doses
                </span>
              </div>
              <Progress value={progressPercent} className="h-3" />
              {allCompleted ? (
                <div className="flex items-center justify-center gap-2 mt-3 py-2 bg-green-500/10 rounded-lg">
                  <PartyPopper size={18} className="text-green-600" />
                  <p className="text-sm font-medium text-green-600">
                    All doses completed for today! 🎉
                  </p>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  {totalCount - completedCount} dose{totalCount - completedCount !== 1 ? 's' : ''} remaining
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Overdue Doses Section (from previous days) */}
        {!loading && overdueDoses.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-sm text-orange-600 flex items-center gap-2">
                <AlertCircle size={16} />
                Missed Doses
              </h3>
              {hasMoreOverdue && !showAllOverdue && (
                <button
                  onClick={() => setShowAllOverdue(true)}
                  className="text-xs text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1"
                  data-testid="view-all-overdue-btn"
                >
                  View all {totalOverdueCount} missed
                  <ChevronDown size={14} />
                </button>
              )}
              {showAllOverdue && hasMoreOverdue && (
                <button
                  onClick={() => setShowAllOverdue(false)}
                  className="text-xs text-muted-foreground hover:text-foreground font-medium"
                >
                  Show less
                </button>
              )}
            </div>
            
            <div className="space-y-2">
              {displayedOverdueDoses.map((dose, index) => (
                <Card
                  key={`overdue-${dose.protocol_id}-${dose.scheduled_date}`}
                  className="border-orange-300/50 bg-orange-50/50 dark:bg-orange-950/20"
                  data-testid={`overdue-dose-${index}`}
                >
                  <CardContent className="p-0">
                    <div className="flex items-center">
                      {/* Log button */}
                      <button
                        onClick={() => handleDoseToggle(dose)}
                        className="flex items-center justify-center w-14 h-full min-h-[72px] border-r border-orange-200/50 bg-orange-100/50 hover:bg-orange-200/50 transition-colors"
                        data-testid={`overdue-log-${index}`}
                        title="Log this dose"
                      >
                        <Circle className="h-6 w-6 text-orange-500" />
                      </button>
                      
                      {/* Content */}
                      <div className="flex-1 px-3 py-2 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <h4 className="font-semibold text-sm truncate">{dose.compound_name}</h4>
                            <p className="text-xs text-orange-600 font-medium">{dose.dose} {dose.dose_unit}</p>
                          </div>
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            <span className="text-xs bg-orange-200/70 text-orange-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                              {format(new Date(dose.scheduled_date + 'T00:00:00'), 'MMM d')}
                            </span>
                            <span className="text-xs text-muted-foreground">{dose.time_of_day}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Skip button */}
                      <button
                        onClick={() => handleSkipDose(dose)}
                        className="flex items-center justify-center w-14 h-full min-h-[72px] border-l border-orange-200/50 bg-orange-50/30 hover:bg-orange-100/50 transition-colors"
                        data-testid={`overdue-skip-${index}`}
                        title="Skip this dose"
                      >
                        <SkipForward className="h-5 w-5 text-muted-foreground hover:text-orange-600" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Today's Doses List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 rounded-2xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : sortedDoses.length > 0 ? (
          <div className="space-y-4">
            {sortedDoses.map((dose, index) => {
              const isOverdue = isDoseOverdue(dose);
              return (
                <Card
                  key={`${dose.protocol_id}-${index}`}
                  className={`protocol-card-enhanced animate-fadeIn stagger-${Math.min(index + 1, 5)} ${
                    dose.is_completed ? "completed" : ""
                  } ${isOverdue ? "border-orange-400/50" : ""}`}
                  style={{ opacity: 0 }}
                  data-testid={`dose-item-${index}`}
                >
                  <CardContent className="p-0">
                    <div className="flex items-stretch">
                      {/* Checkbox Area */}
                      <button
                        onClick={() => handleDoseToggle(dose)}
                        className={`flex items-center justify-center w-16 border-r border-border/50 transition-colors ${
                          isOverdue ? "bg-orange-500/10 hover:bg-orange-500/20" : "bg-muted/30 hover:bg-muted/50"
                        }`}
                        data-testid={`dose-checkbox-${index}`}
                      >
                        {dose.is_completed ? (
                          <CheckCircle2 className="h-8 w-8 text-green-500" />
                        ) : isOverdue ? (
                          <AlertCircle className="h-8 w-8 text-orange-500" />
                        ) : (
                          <Circle className="h-8 w-8 text-muted-foreground/50" />
                        )}
                      </button>
                      
                      {/* Content Area */}
                      <div className="flex-1 p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className={`font-heading font-bold text-lg ${
                              dose.is_completed ? "line-through text-muted-foreground" : ""
                            }`}>
                              {dose.compound_name}
                            </h3>
                            <p className={`text-base font-medium ${
                              dose.is_completed ? "text-muted-foreground" : "text-primary"
                            }`}>
                              {dose.dose} {dose.dose_unit}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-1.5 text-sm text-muted-foreground bg-muted px-2.5 py-1 rounded-full">
                              <Clock size={14} />
                              <span className="font-mono">{dose.time_of_day}</span>
                            </div>
                            {isOverdue && (
                              <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">
                                Overdue
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Protocol Details */}
                        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                          <span className="bg-secondary px-2 py-0.5 rounded-md">
                            {dose.frequency_label}
                          </span>
                          {dose.dose_number && dose.total_doses && (
                            <span className="bg-secondary px-2 py-0.5 rounded-md">
                              Dose {dose.dose_number} of {dose.total_doses}
                            </span>
                          )}
                          {/* Skip button for pending doses */}
                          {!dose.is_completed && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSkipDose(dose);
                              }}
                              className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-orange-600 transition-colors px-2 py-1 rounded-md hover:bg-orange-50"
                              data-testid={`skip-dose-${index}`}
                            >
                              <SkipForward size={12} />
                              Skip
                            </button>
                          )}
                        </div>
                        
                        {/* Notes */}
                        {dose.notes && (
                          <p className="mt-2 text-sm text-muted-foreground italic border-l-2 border-primary/30 pl-2">
                            {dose.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="animate-fadeIn">
            <CardContent className="empty-state py-12">
              <Calendar className="empty-state-icon" strokeWidth={1} />
              <h3 className="empty-state-title">No doses scheduled</h3>
              <p className="empty-state-description">
                You don't have any protocols scheduled for today.
              </p>
              <Link to="/protocols">
                <Button className="mt-6 h-12 rounded-full px-6" data-testid="add-protocol-btn">
                  <Plus size={18} className="mr-2" />
                  Add Protocol
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Upcoming Section */}
        {!loading && upcomingDays.length > 0 && (
          <div className="space-y-3 animate-fadeIn" style={{ animationDelay: "0.3s", opacity: 0 }}>
            <div className="flex items-center gap-2 px-1">
              <CalendarDays size={18} className="text-muted-foreground" />
              <h2 className="font-heading font-semibold text-base text-muted-foreground">
                Upcoming (Next 48 Hours)
              </h2>
            </div>
            
            {upcomingDays.map((dayLabel, dayIndex) => (
              <div key={dayLabel} className="space-y-2">
                {/* Day Header */}
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-1">
                  {dayLabel}
                </p>
                
                {/* Doses for this day */}
                {groupedUpcoming[dayLabel].map((dose, index) => (
                  <Card
                    key={`${dose.protocol_id}-${dayLabel}-${index}`}
                    className="upcoming-card"
                    data-testid={`upcoming-item-${dayIndex}-${index}`}
                  >
                    <CardContent className="p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                          <Clock size={16} className="text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            {dose.compound_name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {dose.dose} {dose.dose_unit}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-mono text-muted-foreground">
                          {dose.time_of_day}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        {!loading && (todayData?.doses?.length > 0 || upcomingDays.length > 0) && (
          <Link to="/protocols">
            <Card className="animate-fadeIn hover:border-primary/50 transition-colors cursor-pointer" style={{ animationDelay: "0.5s", opacity: 0 }}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Plus size={20} className="text-primary" />
                  </div>
                  <span className="font-medium">Manage Protocols</span>
                </div>
                <ChevronRight size={20} className="text-muted-foreground" />
              </CardContent>
            </Card>
          </Link>
        )}
      </main>
    </div>
  );
}
